import React, { useEffect, useRef } from 'react';
import { doc, setDoc, getDocs, query, collection, where } from 'firebase/firestore';
import { formatTime } from '../App';

const ResultScreen = ({
  isGameWon,
  username,
  userPfp,
  selectedPuzzle,
  gameTime,
  difficulty,
  setCurrentScreen,
  db,
  userId,
  appId,
  isAuthReady,
  setErrorMessage,
}) => {
  const hasSaved = useRef(false); // ✅ Prevent duplicate saves

  useEffect(() => {
    if (!isGameWon || !db || !isAuthReady || hasSaved.current) return;

    const saveScore = async () => {
      try {
        const leaderboardRef = collection(db, 'players');
        const q = query(
          leaderboardRef,
          where('userId', '==', userId),
          where('puzzleId', '==', selectedPuzzle?.id),
          where('difficulty', '==', difficulty)
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          await setDoc(doc(leaderboardRef), {
            username,
            pfp: userPfp,
            time: gameTime,
            userId,
            puzzleId: selectedPuzzle.id,
            puzzleName: selectedPuzzle.name,
            difficulty,
            completed: true,
            timestamp: Date.now(),
          });
        } else {
          snapshot.forEach(async (docSnap) => {
            const existing = docSnap.data();
            if (gameTime < existing.time) {
              await setDoc(doc(db, 'players', docSnap.id), {
                ...existing,
                time: gameTime,
                timestamp: Date.now(),
              });
            }
          });
        }

        hasSaved.current = true; // ✅ Mark as saved
      } catch (error) {
        console.error('Error saving result:', error);
        setErrorMessage('Failed to save result. Please try again.');
      }
    };

    saveScore();
  }, [
    isGameWon,
    db,
    isAuthReady,
    userId,
    selectedPuzzle,
    gameTime,
    difficulty,
    userPfp,
    username,
    setErrorMessage,
  ]);

  return (
    <div className="flex flex-col items-center justify-center h-full p-4 text-center">
      <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full border-4 border-pink-500">
        <h2 className="text-3xl font-extrabold mb-6 text-orange-600">Game Result</h2>

        {isGameWon ? (
          <>
            <p className="text-gray-700 text-xl mb-4">
              🎉 Congratulations, <span className="font-bold">@{username}</span>!
            </p>
            <p className="text-lg text-gray-800 mb-6">
              You completed{' '}
              <span className="font-semibold text-pink-600">{selectedPuzzle?.name}</span> on{' '}
              <span className="font-semibold text-orange-500">{difficulty}</span> level in{' '}
              <span className="font-bold">{formatTime(gameTime)}</span>.
            </p>
          </>
        ) : (
          <p className="text-gray-700 text-xl mb-6">
            😢 You didn’t complete the puzzle in time.
          </p>
        )}

        <button
          onClick={() => setCurrentScreen('leaderboard')}
          className="w-full bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold py-4 px-8 rounded-lg shadow-lg hover:from-pink-600 hover:to-orange-600 transition duration-300 transform hover:scale-105 text-xl"
        >
          View Leaderboard
        </button>

        <button
          onClick={() => setCurrentScreen('selectPuzzle')}
          className="mt-4 w-full bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-lg shadow-md hover:bg-gray-400 transition duration-300 text-lg"
        >
          Try Another Puzzle
        </button>
      </div>
    </div>
  );
};

export default ResultScreen;
